import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Card, CardActionArea, CircularProgress, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import VerifiedIcon from "@mui/icons-material/Verified";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import useRawVideos from "./useRawVideos";
import RawClipGrid from "./RawClipGrid";

/**
 * Lets a user choose between the WGID's single watermarked/edited video (the
 * default) or its raw video files, one or more of which are merged back-to-
 * back as separate VRE clips instead.
 *
 * Renders nothing when there are no raw videos to choose from — the existing
 * single-video behaviour is already correct there. `onSelectionChange` is
 * called with the checked raw videos, always in the fixed order Admin Portal
 * returned them (no reorder control) regardless of click order, or `null` for
 * "use the watermarked/edited video".
 */
export default function RawVideoPicker({ wgid, onSelectionChange, initialSelectedIds = null, onIdsChange = null }) {
  const { status, rawVideos, watermarkUrl, error } = useRawVideos(wgid, Boolean(wgid));
  // `initialSelectedIds` restores an earlier pick (a re-edit, a restored draft)
  // and `onIdsChange` reports the pick so the caller can keep it.
  const hasInitial = Array.isArray(initialSelectedIds) && initialSelectedIds.length > 0;
  const [mode, setMode] = useState(hasInitial ? "raw" : "edited"); // "edited" | "raw"
  const [selectedIds, setSelectedIds] = useState(hasInitial ? initialSelectedIds : []);
  const onIdsChangeRef = useRef(onIdsChange);
  onIdsChangeRef.current = onIdsChange;

  // Follow a pick made elsewhere (the batch editor writes the card's
  // rawVideoIds): [] = the edited video, ids = those clips. null/undefined is
  // "nothing decided", which never overrides what is shown here.
  const initialKey = Array.isArray(initialSelectedIds) ? JSON.stringify(initialSelectedIds) : null;
  // Raw mode with nothing ticked yet is its own state, not "the edited video".
  const shownKey = mode === "edited" ? "[]" : selectedIds.length > 0 ? JSON.stringify(selectedIds) : "raw:none";
  useEffect(() => {
    if (initialKey === null || initialKey === shownKey) return;
    const ids = JSON.parse(initialKey);
    setMode(ids.length > 0 ? "raw" : "edited");
    setSelectedIds(ids);
    // Only an outside change of the prop matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);

  // Only a genuine change of video clears the pick, not the first mount.
  const lastWgid = useRef(wgid);
  useEffect(() => {
    if (lastWgid.current === wgid) return;
    lastWgid.current = wgid;
    setSelectedIds([]);
    setMode("edited");
  }, [wgid]);

  // Every clip, in Admin Portal's order — what Raw starts with when nothing is
  // picked yet (user request 2026-10-06: all raw clips ticked by default).
  const allIds = () => rawVideos.map((v) => v.id);

  // True for the one render where the start-up switch below has changed the
  // pick but React has not shown it yet. Reporting then would send the OLD
  // pick ("edited") right before the new one, and the card, echoing both back
  // through `initialSelectedIds`, flipped the two forever — remounting the
  // batch editor on every flip, stuck on "Loading the brand template…".
  const switching = useRef(false);

  // Start on the raw videos whenever there are some — unless the user already
  // chose the edited video explicitly (`initialSelectedIds` is `[]`, not
  // null/undefined). With no edited video to fall back to, raw is the only way.
  // Runs before the report below, so the report can skip the stale render.
  const explicitEdited = Array.isArray(initialSelectedIds) && initialSelectedIds.length === 0;
  useEffect(() => {
    if (status !== "ready" || rawVideos.length === 0) return;
    if (!watermarkUrl || !explicitEdited) {
      // A restored pick stays as it was; otherwise every clip starts ticked.
      if (mode !== "raw" || selectedIds.length === 0) switching.current = true;
      setMode("raw");
      setSelectedIds((prev) => (prev.length > 0 ? prev : allIds()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, watermarkUrl, rawVideos.length, wgid]);

  // Report the pick once the clip list is known, so a not-yet-loaded picker
  // can't overwrite a stored selection with an empty one.
  useEffect(() => {
    if (status !== "ready" || !onIdsChangeRef.current) return;
    if (switching.current) {
      switching.current = false;
      return;
    }
    // [] = the edited video was chosen; null = raw mode with no clip ticked yet.
    onIdsChangeRef.current(mode === "edited" ? [] : selectedIds.length > 0 ? selectedIds : null);
  }, [status, mode, selectedIds]);

  const orderedSelected = useMemo(
    () => rawVideos.filter((v) => selectedIds.includes(v.id)),
    [rawVideos, selectedIds],
  );

  useEffect(() => {
    if (mode === "edited" || rawVideos.length === 0) {
      onSelectionChange(null);
      return;
    }
    onSelectionChange(orderedSelected.length > 0 ? orderedSelected : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, orderedSelected, rawVideos.length]);

  if (status === "loading") {
    return (
      <Box display="flex" alignItems="center" gap={1} mt={1}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Checking for raw video files…
        </Typography>
      </Box>
    );
  }

  if (status === "error") {
    return (
      <Alert severity="warning" sx={{ py: 0, px: 1, fontSize: "0.75rem", mt: 1 }}>
        Could not check for raw video files{error?.response?.status === 404 ? "" : " — using the watermarked/edited video"}.
      </Alert>
    );
  }

  if (rawVideos.length === 0) return null;

  const editedAvailable = Boolean(watermarkUrl);

  return (
    <Box mt={1.5}>
      <Stack direction="row" spacing={1} mb={mode === "raw" ? 1 : 0}>
        <SourceTile
          active={mode === "edited"}
          disabled={!editedAvailable}
          icon={<VerifiedIcon fontSize="small" />}
          title="Edited Video"
          subtitle={editedAvailable ? "Watermarked / final cut" : "Not available"}
          onClick={() => {
            if (!editedAvailable) return;
            setMode("edited");
            setSelectedIds([]);
          }}
        />
        <SourceTile
          active={mode === "raw"}
          icon={<MovieCreationOutlinedIcon fontSize="small" />}
          title="Raw Videos"
          subtitle={`${rawVideos.length} available`}
          onClick={() => {
            setMode("raw");
            setSelectedIds((prev) => (prev.length > 0 ? prev : allIds()));
          }}
        />
      </Stack>

      {mode === "raw" && (
        <RawClipGrid
          rawVideos={rawVideos}
          selectedIds={selectedIds}
          onToggle={(id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))}
        />
      )}
    </Box>
  );
}

function SourceTile({ active, disabled, icon, title, subtitle, onClick }) {
  return (
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        opacity: disabled ? 0.5 : 1,
        borderColor: active ? "primary.main" : "divider",
        borderWidth: active ? 2 : 1,
        bgcolor: active ? (t) => alpha(t.palette.primary.main, 0.06) : "transparent",
        transition: "border-color .15s, background-color .15s",
      }}
    >
      <CardActionArea
        disabled={disabled}
        onClick={onClick}
        sx={{ px: 1.25, py: 0.75, display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-start" }}
      >
        <Box sx={{ color: active ? "primary.main" : "text.secondary", display: "flex" }}>{icon}</Box>
        <Box textAlign="left">
          <Typography variant="caption" fontWeight={700} display="block" lineHeight={1.3}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" lineHeight={1.3}>
            {subtitle}
          </Typography>
        </Box>
      </CardActionArea>
    </Card>
  );
}
